import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../utils/api';
import { useActivityLogStore } from './activityLogStore';

const IDLE_TIMEOUT_MS = 12 * 60 * 60 * 1000; // 12 hour idle timeout (POS + manager; was 1 hour until Oct 2026)

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      lastActivity: Date.now(),
      _idleTimer: null,

      login: async (username, password, totpCode, forceLogin) => {
        try {
          const response = await api.post('/auth/staff-login', { username, password, totpCode, forceLogin });
          const data = response.data;

          // 2FA required — user has MFA enabled
          if (data.requires2FA) {
            return { success: false, requires2FA: true, message: data.message };
          }

          // 2FA setup required — first time MFA user
          if (data.requires2FASetup) {
            return { success: false, requires2FASetup: true, setupToken: data.setupToken, message: data.message };
          }

          // Active session on another device
          if (data.activeSessionExists) {
            return { success: false, activeSessionExists: true, staffName: data.staffName, message: data.message };
          }

          const { token, staff } = data;

          set({
            user: staff,
            token,
            isAuthenticated: true,
            lastActivity: Date.now()
          });

          // Set token in API instance
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          // Clear cart on new session start
          try { await api.post('/cart/clear'); } catch (e) { /* ignore — cart may already be empty */ }

          // Start idle timeout monitoring
          get()._startIdleMonitor();

          useActivityLogStore.getState().logLogin(staff.staffName || staff.name || staff.username);
          return { success: true };
        } catch (error) {
          console.error('Login error:', error);
          // Check if session was revoked (force-logged out by another device)
          if (error.response?.data?.sessionRevoked) {
            get().logout();
            return { success: false, error: 'Session revoked. Another device logged in.' };
          }
          // Clear any stale auth state on failed login
          set({
            user: null,
            token: null,
            isAuthenticated: false
          });
          delete api.defaults.headers.common['Authorization'];
          return {
            success: false,
            requires2FA: error.response?.data?.requires2FA || false,
            activeSessionExists: error.response?.data?.activeSessionExists || false,
            staffName: error.response?.data?.staffName,
            message: error.response?.data?.message,
            error: error.response?.data?.error || 'Login failed'
          };
        }
      },

      logout: async () => {
        // Clear idle timer
        const timer = get()._idleTimer;
        if (timer) clearTimeout(timer);

        // Clear auth state FIRST to prevent stale token issues on next login
        const oldToken = api.defaults.headers.common['Authorization'];
        useActivityLogStore.getState().logLogout();
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          lastActivity: null,
          _idleTimer: null
        });
        delete api.defaults.headers.common['Authorization'];
        localStorage.removeItem('auth-storage');
        localStorage.removeItem('selectedLocation');
        // Clear the in-progress checkout draft (customer / staff / payment / address) on logout, so
        // the next user who logs in on this device never sees the previous manager's draft.
        try { document.cookie = 'pos_checkout_draft=;path=/;max-age=0;samesite=lax'; } catch (e) { /* non-fatal */ }

        // Fire-and-forget logout event to server (non-blocking)
        try {
          await api.post('/auth/staff-logout', {}, {
            headers: { 'Authorization': oldToken }
          });
        } catch (error) {
          // Ignore — auth state already cleared
        }
      },

      // Update activity timestamp (called on user interactions)
      touchActivity: () => {
        set({ lastActivity: Date.now() });
        get()._startIdleMonitor();
      },

      // Internal: start/restart the idle timeout timer
      _startIdleMonitor: () => {
        const existing = get()._idleTimer;
        if (existing) clearTimeout(existing);

        const timer = setTimeout(() => {
          const { lastActivity, isAuthenticated } = get();
          if (!isAuthenticated) return;
          const elapsed = Date.now() - lastActivity;
          if (elapsed >= IDLE_TIMEOUT_MS) {
            console.log('⏰ Idle timeout reached — logging out');
            get().logout();
            window.location.href = '/login?reason=idle';
          }
        }, IDLE_TIMEOUT_MS);

        set({ _idleTimer: timer });
      },

      initializeAuth: (token) => {
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          get()._startIdleMonitor();
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        lastActivity: state.lastActivity
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          // Validate token is not expired before restoring session
          try {
            const payload = JSON.parse(atob(state.token.split('.')[1]));
            if (payload.exp && payload.exp * 1000 < Date.now()) {
              // Token expired — clear auth state
              state.user = null;
              state.token = null;
              state.isAuthenticated = false;
              delete api.defaults.headers.common['Authorization'];
              return;
            }
          } catch {
            // Invalid token format — clear auth
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            delete api.defaults.headers.common['Authorization'];
            return;
          }

          // Check idle timeout on rehydrate
          if (state.lastActivity && (Date.now() - state.lastActivity) >= IDLE_TIMEOUT_MS) {
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            delete api.defaults.headers.common['Authorization'];
            return;
          }

          api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
        }
      }
    }
  )
);
